import type { ReactNode } from 'react';
import React, { useCallback, useContext, useEffect, useState } from 'react';

// eslint-disable-next-line @atlaskit/design-system/use-spotlight-package
import { SpotlightTarget, SpotlightTransition } from '@atlaskit/onboarding';
import { PopoverProvider, PopoverTarget } from '@atlaskit/spotlight';
import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import { token } from '@atlaskit/tokens';

// eslint-disable-next-line no-restricted-imports
import { isFedRamp } from '@atlassian/atl-context';
import {
	EditionAwarenessWithRelayEnvironmentProvider,
	type Edition,
	type Flag,
} from '@atlassian/edition-awareness';

import { SPAViewContext } from '@confluence/spa-view-context/entry-points/SPAViewContext.ts';
import { fg } from '@confluence/feature-gating';
import { useWindowSize } from '@confluence/dom-helpers/entry-points/useWindowSize.ts';
import { withFlags } from '@confluence/flags/entry-points/withFlags.ts';
import type { FlagsStateContainer } from '@confluence/flags/entry-points/FlagsStateContainer.ts';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';
import { useRenderServerPlaceholder } from '@confluence/ssr-utilities/entry-points/useRenderServerPlaceholder.ts';
import {
	AccessStatus,
	ConfluenceEdition,
} from '@confluence/session-data/entry-points/SiteInformationQuery.ts';
import { useReverseTrialCohort } from '@confluence/trial-components/entry-points/useReverseTrialEligible.tsx';
import { PremiumFeatureHighlightToggle } from '@confluence/premium-onboarding/entry-points/PremiumFeatureHighlightToggle.ts';
import { PremiumTrialOnboardingSpotlight } from '@confluence/premium-onboarding/entry-points/PremiumTrialOnboardingSpotlight.ts';
import {
	TriggerFrom,
	PremiumTrialOnboardingModal,
} from '@confluence/premium-onboarding/entry-points/PremiumTrialOnboardingModal.ts';
import { BorderNudge } from '@confluence/onboarding-hover-nudge/entry-points/BorderNudge.ts';
import { HOVER_TARGET } from '@confluence/onboarding-hover-nudge/entry-points/constants/HoverTarget.ts';
import {
	PTO_PHASE_TWO_SPOTLIGHT_TARGET,
	getIsPremiumOnboardingHappened,
	useShouldCheckEligibilityForSpotlight,
} from '@confluence/premium-onboarding/entry-points/useShouldCheckEligibilityForSpotlight.ts';
import { ConfluenceTrialSpotlight } from '@confluence/auto-provisioning-edition-parity/entry-points/ConfluenceTrialSpotlight.ts';
import {
	useOpenActionMenuActions,
	useOpenActionMenuState,
} from '@confluence/trial-components/entry-points/useOpenActionMenu.ts';
import { OnboardingPagePermissionsSpotlight } from '@confluence/share-and-restrict-dialog/entry-points/OnboardingPagePermissionsSpotlight.ts';

import type { Trait } from './FetchSiteTraits.tsx';
import { getSiteTraits } from './FetchSiteTraits.tsx';
import { useActions } from './useActions.ts';

export type SiteType = 'EDU' | 'ORG' | 'UNKNOWN' | null;

const editionMap: Record<ConfluenceEdition, Edition> = {
	[ConfluenceEdition.FREE]: 'free',
	[ConfluenceEdition.STANDARD]: 'standard',
	[ConfluenceEdition.PREMIUM]: 'premium',
};

type PremiumWrapperProps = {
	children: ReactNode;
	isInTrial: boolean;
	isInReverseTrial: boolean;
	trialDaysLeft?: number;
	isPremiumModalOpen: boolean;
	onClosePremiumModal: () => void;
	isSiteAdmin: boolean;
};

export function PremiumWrapper({
	children,
	isInTrial,
	isInReverseTrial,
	trialDaysLeft,
	isPremiumModalOpen,
	onClosePremiumModal,
	isSiteAdmin,
}: PremiumWrapperProps) {
	const { setIsOpen } = useOpenActionMenuActions();
	// PopoverProvider requires client-side DOM measurements for proper positioning, e.g. getBoundingClientRect()
	// During SSR, positioning calculations fail and cause errors
	const renderServerPlaceholder = useRenderServerPlaceholder();

	const [{ isSpotlightCheckEnabled }, { enableSpotlightCheck }] =
		useShouldCheckEligibilityForSpotlight();

	useEffect(() => {
		async function ptoPhaseTwoSpotlightCheck() {
			if (isSpotlightCheckEnabled === false && getIsPremiumOnboardingHappened() && isInTrial) {
				await enableSpotlightCheck();
			}
		}
		void ptoPhaseTwoSpotlightCheck();
	}, [isSpotlightCheckEnabled, enableSpotlightCheck, isInTrial]);

	/**
	 * Renders the new @atlaskit/spotlight implementation (PopoverProvider/PopoverTarget).
	 * Only renders the spotlight when in trial and not in reverse trial.
	 * See ticket ENGHEALTH-46607 for migration details.
	 */
	const renderNewSpotlight = () => {
		if (isInTrial && !isInReverseTrial) {
			return (
				<PopoverProvider>
					{/* BorderNudge removed: pulse animation is not available in new @atlaskit/spotlight API */}
					<PopoverTarget>{children}</PopoverTarget>
					<PremiumTrialOnboardingSpotlight
						daysLeft={trialDaysLeft}
						isSiteAdmin={isSiteAdmin}
						renderServerPlaceholder={renderServerPlaceholder}
					/>
				</PopoverProvider>
			);
		}
		return children;
	};

	return (
		<>
			{isPremiumModalOpen && (
				<PremiumTrialOnboardingModal
					onClose={onClosePremiumModal}
					triggerFrom={TriggerFrom.PILL}
					daysLeft={trialDaysLeft}
				/>
			)}
			{fg('cc-engagement-migrate-premium-trial-spotlight') ? (
				renderNewSpotlight()
			) : (
				<>
					<BorderNudge
						target={HOVER_TARGET.PREMIUM_TRIAL}
						customStyles={{
							width: '100%',
							borderRadius: token('radius.large'),
						}}
					>
						{isInTrial && !isInReverseTrial ? (
							<SpotlightTarget name={PTO_PHASE_TWO_SPOTLIGHT_TARGET}>{children}</SpotlightTarget>
						) : (
							children
						)}
					</BorderNudge>
					{isInTrial && (
						<PremiumTrialOnboardingSpotlight daysLeft={trialDaysLeft} isSiteAdmin={isSiteAdmin} />
					)}
				</>
			)}
			{fg('d0_confluence_auto_provisioning_onboarding_upgrade') && (
				<ConfluenceTrialSpotlight onClose={() => setIsOpen(true)} />
			)}
		</>
	);
}

const EditionAwarenessFlagSupport = ({
	flags,
	...props
}: { flags: FlagsStateContainer } & React.ComponentProps<
	typeof EditionAwarenessWithRelayEnvironmentProvider
>) => {
	const showFlag = useCallback((flag: Flag) => flags.showFlag(flag), [flags]);

	return (
		<EditionAwarenessWithRelayEnvironmentProvider
			{...props}
			showFlag={showFlag}
			hideFlag={flags.hideFlag}
		/>
	);
};

const EditionAwarenessWithFlags = withFlags(EditionAwarenessFlagSupport);

export const EditionAwareness = () => {
	const {
		edition: confluenceEdition,
		cloudId,
		accessStatus,
		isLoggedIn,
		userId,
		displayName,
	} = useSessionData();
	const { isSiteAdmin } = useContext(SPAViewContext);
	const { createAnalyticsEvent } = useAnalyticsEvents();

	const [isPagePermissionsSpotlightActive, setIsPagePermissionsSpotlightActive] = useState(false);
	const showPagePermissionsSpotlight = useCallback(() => {
		setIsPagePermissionsSpotlightActive(true);
	}, []);
	const hidePagePermissionsSpotlight = useCallback(() => {
		setIsPagePermissionsSpotlightActive(false);
	}, []);

	const getAction = useActions({ showPagePermissionsSpotlight });

	const [siteTraits, setSiteTraits] = useState<Trait[] | undefined>(() => {
		if (confluenceEdition === ConfluenceEdition.FREE) {
			return undefined;
		}

		return [];
	});
	const isFedrampEnv = isFedRamp();
	const [autoOpenState, { setIsOpen }] = useOpenActionMenuState();
	const { width: windowWidth } = useWindowSize();

	const resetAutoOpenState = useCallback(() => {
		setIsOpen(false);
	}, [setIsOpen]);

	const isLicensed =
		accessStatus === AccessStatus.LICENSED_ADMIN_ACCESS ||
		accessStatus === AccessStatus.LICENSED_USE_ACCESS;

	useEffect(() => {
		if (siteTraits === undefined) {
			getSiteTraits(cloudId)
				.then((traits) => {
					setSiteTraits(traits);
				})
				.catch((error) => {
					setSiteTraits([]);
					createAnalyticsEvent({
						type: 'sendOperationalEvent',
						data: {
							action: 'failed',
							actionSubject: 'editionAwarenessGrowthCohortTrait',
							source: 'confluence-frontend',
							attributes: {
								error: error.message,
								stack: error.stack,
							},
						},
					}).fire();
				});
		}
	}, [cloudId, confluenceEdition, createAnalyticsEvent, siteTraits]);

	const edition = confluenceEdition ? editionMap[confluenceEdition] : null;

	const { isReverseTrialer, loading: isReverseTrialLoading } = useReverseTrialCohort({
		skip:
			!isLicensed || !isLoggedIn || isFedrampEnv || confluenceEdition !== ConfluenceEdition.PREMIUM,
	});

	// TODO: Once this component is SSRed, change this logic to use a media query
	if (windowWidth < 680) {
		return null;
	}

	if (!isLicensed || !isLoggedIn || isFedrampEnv || !edition) {
		// Users that are not logged in/licensed shouldn't attempt to render the component
		// If we're missing the edition then we don't know what to render
		return null;
	}

	// We need to wait for the siteType to be set, and isReverseTrialer to be known before we can render the button
	const canRender = siteTraits !== undefined && !isReverseTrialLoading;

	const componentContent = (
		<EditionAwarenessWithFlags
			product="confluence"
			cloudId={cloudId}
			edition={edition}
			accountId={userId}
			userDisplayName={displayName}
			userRoles={isSiteAdmin ? ['SITE_ADMIN'] : []}
			siteTraits={siteTraits}
			isInReverseTrial={isReverseTrialer}
			premiumFeatureHighlightToggle={
				<PremiumFeatureHighlightToggle
					id="prem-feature-highlight-toggle-pill"
					source="premTrialActionComponent"
				/>
			}
			shouldAutoOpen={autoOpenState.isOpen}
			// While this is called onAutoOpen, we are calling it when the dialog closes to reset the state if we reset on open, the dialog would never open
			onAutoOpen={resetAutoOpenState}
			PremiumWrapper={PremiumWrapper}
			canRender={canRender}
			getAction={getAction}
		/>
	);

	return (
		<div data-testid="edition-awareness">
			{fg('confluence_frontend_spotlight_ads_migration') ? (
				<PopoverProvider>
					<PopoverTarget>{componentContent}</PopoverTarget>
					{isPagePermissionsSpotlightActive ? (
						<OnboardingPagePermissionsSpotlight onClose={hidePagePermissionsSpotlight} />
					) : null}
				</PopoverProvider>
			) : (
				<>
					<SpotlightTarget name="edition-awareness">{componentContent}</SpotlightTarget>
					<SpotlightTransition>
						{isPagePermissionsSpotlightActive ? (
							<OnboardingPagePermissionsSpotlight onClose={hidePagePermissionsSpotlight} />
						) : null}
					</SpotlightTransition>
				</>
			)}
		</div>
	);
};
